import BaseRequest from './BaseRequest';
import HTTPRequest from '../HTTPRequest';
import Auth from './Auth';
import Venues from './Venues';
import { getTeamUpCustomerFieldValue } from '../Utilities';

export default class Customers extends BaseRequest {
    /**
     * Customers constructor
     *
     * @param {string} requestMode
     * @param {string} providerId
     * @param {string} customerId
     * @param {boolean} auth
     * @param {boolean} fowardRequest
     */
    constructor(
        requestMode = 'customer',
        providerId = '',
        customerId = '',
        auth = true,
        fowardRequest = false,
    ) {
        super(requestMode, providerId, customerId, auth, fowardRequest);
        this.setClassName('Customers');
        this.setEndpoint('customers');
    }

    /**
     * @return {object}
     */
    async accountStatus() {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/${this.getCustomerId()}/account_status`,
            this.getHeaders(),
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'accountStatus',
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} email
     * @param {string} currentPassword
     * @return {object}
     */
    async changeEmail(email = '', currentPassword = '') {
        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/${this.getCustomerId()}/change_email`,
            this.getHeaders(),
            {},
            {
                current_password: currentPassword,
                new_email: email,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'changeEmail',
                {
                    email: email,
                    currentPassword: currentPassword,
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} oldPassword
     * @param {string} newPassword
     * @return {object}
     */
    async changePassword(oldPassword = '', newPassword = '') {
        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/${this.getCustomerId()}/change_password`,
            this.getHeaders(),
            {},
            {
                new_password: newPassword,
                old_password: oldPassword,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'changePassword',
                {
                    oldPassword: oldPassword,
                    newPassword: newPassword,
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @return {object}
     */
    async notificationPreferences() {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/${this.getCustomerId()}/notification_preferences`,
            this.getHeaders(),
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'notificationPreferences',
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} notificationPreferenceId
     * @param {boolean} emailEnabled
     * @param {boolean} smsEnabled
     * @return {object}
     */
    async updateNotificationPreferences(
        notificationPreferenceId = '',
        emailEnabled = false,
        smsEnabled = false,
    ) {
        let request = new HTTPRequest(
            'PATCH',
            `${this.getEndpoint()}/${this.getCustomerId()}/notification_preferences/${notificationPreferenceId}`,
            this.getHeaders(),
            {},
            {
                email_enabled: emailEnabled,
                sms_enabled: smsEnabled,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'updateNotificationPreferences',
                {
                    notificationPreferenceId: notificationPreferenceId,
                    emailEnabled: emailEnabled,
                    smsEnabled: smsEnabled,
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {boolean} participating
     * @param {array} fieldValues
     * @return {object}
     */
    async participation(participating = true, fieldValues = []) {
        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/${this.getCustomerId()}/participation`,
            this.getHeaders(),
            {},
            {
                field_values: fieldValues,
                participating: participating,
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'participation',
                {
                    participating: participating,
                    fieldValues: fieldValues,
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @return {object}
     */
    async resendEmailConfirmation() {
        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/${this.getCustomerId()}/resend_email_confirmation`,
            this.getHeaders(),
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'resendEmailConfirmation',
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} providerId
     * @param {string} email
     * @param {string} firstName
     * @param {string} lastName
     * @param {array} fieldValues
     * @param {string} referralCode
     * @param {integer} venue
     * @param {string} verificationKey
     * @return {object}
     */
    async registerProvider(
        providerId = '',
        email = '',
        firstName = '',
        lastName = '',
        fieldValues = [],
        referralCode = '',
        venue = null,
        verificationKey = '',
    ) {
        this.setCustomerId('');
        if (providerId != '') {
            this.setProviderId(providerId);
        }

        let body = {
            email: email,
            first_name: firstName,
            last_name: lastName,
            field_values: fieldValues,
        };

        if (referralCode !== '') {
            body['referral_code'] = referralCode;
        }
        if (venue !== null) {
            body['venue'] = venue;
        }
        if (verificationKey !== '') {
            body['verification_key'] = verificationKey;
        }

        let request = new HTTPRequest(
            'POST',
            this.getEndpoint(),
            this.getHeaders(),
            {},
            body,
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'registerProvider',
                {
                    providerId,
                    email,
                    firstName,
                    lastName,
                    fieldValues,
                    referralCode,
                    venue,
                    verificationKey
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {integer} providerId
     * @param {string} verificationKey
     * @return {object}
     */
    async registerCustomerToProvider(providerId = '', verificationKey = '') {
        let email = '';
        let firstName = '';
        let lastName = '';
        let fieldValues = [];
        let venue = null;

        const firstProfile = await this.getFirstCustomerProfile();

        if (firstProfile !== null) {
            email = firstProfile.email;
            firstName = firstProfile.first_name;
            lastName = firstProfile.last_name;

            const gender = getTeamUpCustomerFieldValue(
                'gender',
                firstProfile.field_values,
            );
            const phone = getTeamUpCustomerFieldValue(
                'phone',
                firstProfile.field_values,
            );
            const address = getTeamUpCustomerFieldValue(
                'address',
                firstProfile.field_values,
            );
            const dateOfBirth = getTeamUpCustomerFieldValue(
                'date_of_birth',
                firstProfile.field_values,
            );

            const whereDidYou = getTeamUpCustomerFieldValue(
                'where_did_you',
                firstProfile.field_values,
            );

            fieldValues = [
                {
                    field: 'phone',
                    value: phone?.value ?? '',
                },
                {
                    field: 'where_did_you',
                    value: whereDidYou?.value ?? '',
                },
                {
                    field: 'gender',
                    value: gender?.value ?? '',
                },
                {
                    field: 'date_of_birth',
                    value: dateOfBirth?.value ?? '',
                },
                {
                    field: 'postcode',
                    value: address?.value?.postcode ?? '',
                },
                {
                    field: 'address',
                    value: {
                        address_line1: address?.value?.address_line1 ?? '',
                        address_line2: address?.value?.address_line2 ?? '',
                        city: address?.value?.city ?? '',
                        country: address?.value?.country ?? '',
                        postcode: address?.value?.postcode ?? '',
                        region: address?.value?.region ?? '',
                    },
                },
            ];
        }

        const venues = new Venues('customer', providerId, false, false);
        const venuesResponse = await venues.list(1, 1);

        if (venuesResponse.errors.length > 0) {
            return venuesResponse;
        }

        if (venuesResponse.results.length > 0) {
            venue = venuesResponse.results[0].id;
        }

        return this.registerProvider(
            providerId,
            email,
            firstName,
            lastName,
            fieldValues,
            '',
            venue,
            verificationKey,
        );
    }

    /**
     * @return {object|null}
     */
    async getFirstCustomerProfile() {
        let customerProfile = null;
        const auth = new Auth('customer');

        let authResponse = await auth.profilesAll();

        if (authResponse.results.length > 0) {
            const firstProfileKey = Object.keys(authResponse.results)[0];
            let firstProfile = authResponse.results[firstProfileKey];

            const currentProviderId = this.getProviderId();
            const currentCustomerId = this.getCustomerId();

            this.setCustomerId(firstProfile?.id);
            this.setProviderId(
                firstProfile?.provider?.id ?? firstProfile?.provider,
            );
            const customerProfileResponse = await this.retrieve(
                firstProfile.id,
                ['field_values', 'field_values.field', 'sms_account'],
            );
            this.setCustomerId(currentCustomerId);
            this.setProviderId(currentProviderId);

            if (customerProfileResponse.results.length > 0) {
                customerProfile = customerProfileResponse.results[0];
            }
        }

        return customerProfile;
    }

    /**
     * @return {object|null}
     */
    async getCurrentCustomerProfile() {
        let customerProfile = null;
        const auth = new Auth('customer');

        let authResponse = await auth.profile();

        if (authResponse.results.length > 0) {
            const firstProfileKey = Object.keys(authResponse.results)[0];
            let firstProfile = authResponse.results[firstProfileKey];

            const currentProviderId = this.getProviderId();
            const currentCustomerId = this.getCustomerId();
            this.setCustomerId(firstProfile?.id);
            this.setProviderId(
                firstProfile?.provider?.id ?? firstProfile?.provider,
            );
            const customerProfileResponse = await this.retrieve(
                firstProfile.id,
                ['field_values', 'field_values.field', 'sms_account'],
            );
            this.setCustomerId(currentCustomerId);
            this.setProviderId(currentProviderId);

            if (customerProfileResponse.results.length > 0) {
                customerProfile = customerProfileResponse.results[0];
            }
        }

        return customerProfile;
    }

    /**
     * @return {object}
     */
    async getSMSAccount() {
        let request = new HTTPRequest(
            'GET',
            `${this.getEndpoint()}/${this.getCustomerId()}/sms_account`,
            this.getHeaders(),
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'smsAccount',
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} country
     * @param {string} phoneNumber
     * @param {boolean} enabled
     * @return {object}
     */
    async updateSMSAccount(country = '', phoneNumber = '', enabled = false) {
        let request = new HTTPRequest(
            'PUT',
            `${this.getEndpoint()}/${this.getCustomerId()}/sms_account`,
            this.getHeaders(),
            {},
            {
                country: country,
                phone_number: phoneNumber,
                enabled: enabled
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'updateSMSAccount',
                {
                    country: country,
                    phoneNumber: phoneNumber,
                    enabled: enabled
                },
            );
        }

        return await request.sendRequest();
    }

    /**
     * @param {string} verificationCode
     * @return {object}
     */
    async verifySMSAccount(verificationCode = '') {
        let request = new HTTPRequest(
            'POST',
            `${this.getEndpoint()}/${this.getCustomerId()}/sms_account/verify`,
            this.getHeaders(),
            {},
            {
                verification_code: verificationCode
            },
        );
        if (this.getForwardRequest()) {
            request = this.buildForwardRequest(
                this.getClassName(),
                'verifySMSAccount',
                {
                    verificationCode: verificationCode
                },
            );
        }

        return await request.sendRequest();
    }
}
